import React, { useEffect, useRef, useState } from 'react';
import {
  Check,
  Sparkles,
  Pencil,
  AlertCircle,
  Trash2,
  RotateCcw,
  ChevronUp,
  ChevronRight,
  Star,
  Clock,
} from 'lucide-react';
import {
  SK_RECENCY,
  SK_STAR_TO_LEVEL,
  SK_RECENCY_META,
  skProfLabel,
  skMonthsHuman,
  skRecencyBand,
  skInferRecency,
  skExpOK,
  skProfOK,
  skMissing,
  skStarCount,
} from './skillHelpers';

/**
 * Compact-row metadata as three visually distinct segments (rather than one flat
 * dot-joined string): mini stars for proficiency, a clock + duration for
 * experience, and a colour-toned freshness dot for recency. Experience uses the
 * value the card shows (the candidate's typed estimate when present, else the
 * inferred figure) so it can't go missing. Segments are omitted when absent.
 * Returns an array of JSX nodes (callers check `.length`).
 */
function buildRowMeta(s) {
  const months = s.inputMonths != null ? s.inputMonths : s.evidenceMonths || 0;
  const sc = skStarCount(s);
  const band = skRecencyBand(s);
  const rm = band ? SK_RECENCY_META[band] : null;
  const segs = [];

  if (sc > 0) {
    segs.push(
      <span
        className="rl-seg rl-prof"
        key="prof"
        title={skProfLabel(s.proficiency)}
      >
        <span
          className="rl-stars"
          role="img"
          aria-label={`Proficiency: ${skProfLabel(s.proficiency)}`}
        >
          {[1, 2, 3, 4].map((i) => (
            <span key={i} className={`rl-star${i <= sc ? ' on' : ''}`}>
              <Star aria-hidden="true" />
            </span>
          ))}
        </span>
      </span>
    );
  }
  if (months > 0) {
    segs.push(
      <span
        className="rl-seg rl-exp"
        key="exp"
        aria-label={`Experience: ${skMonthsHuman(months)}`}
      >
        <Clock aria-hidden="true" />
        <span>{skMonthsHuman(months)}</span>
      </span>
    );
  }
  if (rm) {
    segs.push(
      <span
        className={`rl-seg rl-rec rl-rec--${rm.tone}`}
        key="rec"
        aria-label={`Last used: ${rm.short}`}
      >
        <span className="rl-dot" />
        <span>{rm.short}</span>
      </span>
    );
  }
  return segs;
}

/**
 * A single skill card. Pure-presentational: all state lives in the page and is
 * passed in; every interaction is a callback. Renders these shapes:
 *  - collapsed confirmed "thin row" (done pile, while reviews remain)
 *  - collapsed pending "thin row" (mobile only — a scannable review queue)
 *  - inline months editor
 *  - full review/confirmed card
 */
export default function SkillCard({
  skill: s,
  editing,
  expanded, // confirmed card expanded out of the done pile
  expandedPending, // mobile: pending row expanded into a full card
  isMobile,
  confirmingRemove,
  onConfirm,
  onEnterEdit,
  onCancelEdit,
  onSaveEstimate,
  onSetStars,
  onSetRecency,
  onSaveConfirmedEdits,
  onSuppress,
  onRestore,
  onExpandConfirmed,
  onCollapseConfirmed,
  onExpandPending,
  onCollapsePending,
  onRequestRemove,
  onCancelRemove,
}) {
  const hasInference = (s.evidenceMonths || 0) > 0;

  const inputRef = useRef(null);
  const [draft, setDraft] = useState('');

  // Live "= 3y 5m" preview of the typed months (not committed until Save/blur).
  const previewMonths = (() => {
    const raw = String(draft).trim();
    if (raw === '') return '';
    let n = Math.round(Number(raw));
    if (Number.isNaN(n) || n < 0) return '';
    if (n > 600) n = 600;
    return n > 0 ? `= ${skMonthsHuman(n)}` : '';
  })();

  // Seed the editor when it opens (input months, else inference, else blank).
  useEffect(() => {
    if (editing) {
      let prefill = '';
      if (s.inputMonths != null) prefill = s.inputMonths;
      else if (hasInference) prefill = s.evidenceMonths;
      setDraft(String(prefill));
      const t = setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select?.();
      }, 0);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [editing, s.inputMonths, s.evidenceMonths, hasInference]);

  // ----- collapsed confirmed thin row (done-pile) -----
  // Confirmed skills always collapse to an accordion row (whether or not any
  // reviews remain); tapping the row expands it into the full card.
  const collapsedConfirmed =
    s.is_confirmed && !editing && !s.is_suppressed && !expanded;
  if (collapsedConfirmed) {
    const meta = buildRowMeta(s);
    return (
      <li
        className="skill-card skill-rowlet is-confirmed"
        data-id={s.id}
        tabIndex={-1}
        aria-label={`${s.name}, confirmed`}
      >
        <button
          type="button"
          className="skill-rowlet-btn"
          onClick={() => onExpandConfirmed(s.id)}
          aria-expanded="false"
        >
          <span className="skill-rowlet-check">
            <Check strokeWidth={2.5} aria-hidden="true" />
          </span>
          <span className="skill-rowlet-text">
            <span className="skill-rowlet-name">{s.name}</span>
            {meta.length > 0 && (
              <span className="skill-rowlet-detail rl-meta">{meta}</span>
            )}
          </span>
          <span className="skill-rowlet-chevron">
            <ChevronRight aria-hidden="true" />
          </span>
        </button>
      </li>
    );
  }

  const miss = skMissing(s);
  const err = !!s.errorFlag;

  // ----- collapsed pending thin row (mobile only) -----
  const collapsedPending =
    !s.is_confirmed &&
    !s.is_suppressed &&
    !editing &&
    isMobile &&
    !expandedPending;
  if (collapsedPending) {
    const ready = miss.length === 0;
    const meta = buildRowMeta(s);
    return (
      <li
        className={`skill-card skill-rowlet skill-rowlet--pending${
          ready ? ' is-ready' : ''
        }`}
        data-id={s.id}
        tabIndex={-1}
        aria-label={`${s.name}, ${
          ready ? 'ready to confirm' : `${miss.length} details to add`
        }`}
      >
        <button
          type="button"
          className="skill-rowlet-btn"
          onClick={() => onExpandPending(s.id)}
          aria-expanded="false"
        >
          <span className="skill-rowlet-flag">
            {ready ? (
              <Check strokeWidth={2.5} aria-hidden="true" />
            ) : (
              <AlertCircle aria-hidden="true" />
            )}
          </span>
          <span className="skill-rowlet-text">
            <span className="skill-rowlet-name">{s.name}</span>
            <span className="skill-rowlet-detail rl-meta">
              {meta.length > 0 ? (
                meta
              ) : (
                <span className="rl-seg rl-empty">Not reviewed yet</span>
              )}
            </span>
          </span>
          {ready ? (
            <span className="skill-rowlet-status is-ready">
              <Check strokeWidth={2.5} aria-hidden="true" />
              Ready
            </span>
          ) : (
            <span className="skill-rowlet-status is-todo">
              {miss.length} to add
            </span>
          )}
          <span className="skill-rowlet-chevron">
            <ChevronRight aria-hidden="true" />
          </span>
        </button>
      </li>
    );
  }

  const need = (k) => miss.indexOf(k) !== -1;
  const expVal = s.inputMonths != null ? s.inputMonths : s.evidenceMonths;
  const reqTag = (isErr) => (
    <span className={`skill-req${isErr ? ' skill-req--error' : ''}`}>
      Required
    </span>
  );

  // ----- experience block -----
  let expBlock;
  if (editing) {
    expBlock = (
      <div
        className={`skill-edit${err && need('experience') ? ' is-error' : ''}`}
      >
        <div className="skill-edit-label">
          How many months have you worked with this?
        </div>
        <div className="skill-edit-row">
          <input
            ref={inputRef}
            className="skill-input"
            id={`skillInput_${s.id}`}
            type="number"
            min="0"
            max="600"
            inputMode="numeric"
            value={draft}
            placeholder="0"
            aria-label={`Months of experience for ${s.name}`}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={(e) => {
              // Commit on blur (click/tab away). Save & Cancel live in
              // .skill-actions and run their own handlers, so skip those.
              const next = e.relatedTarget;
              if (next && next.closest && next.closest('.skill-actions'))
                return;
              onSaveEstimate(s.id, e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                onSaveEstimate(s.id, draft);
              } else if (e.key === 'Escape') {
                e.preventDefault();
                onCancelEdit(s.id);
              }
            }}
          />
          <span className="skill-input-unit">months</span>
          <span className="skill-edit-preview">{previewMonths}</span>
        </div>
      </div>
    );
  } else if (s.is_confirmed) {
    expBlock = (
      <button
        type="button"
        className="skill-exp skill-exp--btn"
        onClick={() => onEnterEdit(s.id)}
        aria-label={`Edit experience for ${s.name}`}
      >
        <div className="skill-exp-label">Your experience</div>
        {expVal > 0 ? (
          <div className="skill-exp-val">
            {skMonthsHuman(expVal)}
            <span className="skill-exp-pencil">
              <Pencil aria-hidden="true" />
            </span>
          </div>
        ) : (
          <div className="skill-exp-none">
            No duration given
            <span className="skill-exp-pencil">
              <Pencil aria-hidden="true" />
            </span>
          </div>
        )}
      </button>
    );
  } else if (skExpOK(s)) {
    const inferred = s.inputMonths == null;
    expBlock = (
      <button
        type="button"
        className="skill-exp skill-exp--btn"
        onClick={() => onEnterEdit(s.id)}
        aria-label={`Edit experience for ${s.name}`}
      >
        <div className="skill-exp-label">
          {inferred ? (
            <>
              <Sparkles aria-hidden="true" />
              Estimated from your work history
            </>
          ) : (
            <>
              <Check strokeWidth={2} aria-hidden="true" />
              Your estimate
            </>
          )}
        </div>
        <div className="skill-exp-val">
          {skMonthsHuman(expVal)}
          <span className="skill-exp-pencil">
            <Pencil aria-hidden="true" />
          </span>
        </div>
      </button>
    );
  } else {
    expBlock = (
      <button
        type="button"
        className={`skill-exp skill-exp--btn skill-exp--add${err ? ' is-error' : ''}`}
        onClick={() => onEnterEdit(s.id)}
        aria-label={`Add experience for ${s.name}`}
      >
        <div className="skill-exp-label">
          {err ? (
            <AlertCircle aria-hidden="true" />
          ) : (
            <Sparkles aria-hidden="true" />
          )}
          Experience
          {reqTag(err)}
        </div>
        <div className="skill-exp-addcta">
          + Add how long you&apos;ve used it
        </div>
      </button>
    );
  }

  // ----- proficiency: 4-star rating (1★ Beginner … 4★ Expert) -----
  // DOM order is reversed (4→1) so a pure-CSS hover preview can fill the hovered
  // star plus every lower one via the sibling combinator (see MySkills.css).
  const profMissing = !skProfOK(s);
  const profErr = err && profMissing;
  const profVal = skStarCount(s); // 0 = unset, else 1..4
  const profRow = (
    <div
      id={`prof_${s.id}`}
      className={`skill-prof${profErr ? ' is-error' : ''}`}
    >
      <div className="skill-prof-label">
        Proficiency
        {profMissing ? reqTag(profErr) : null}
      </div>
      <div className="skill-stars-row">
        <div
          className="skill-stars"
          role="radiogroup"
          aria-label={`Proficiency for ${s.name}`}
        >
          {[4, 3, 2, 1].map((si) => {
            const lvl = SK_STAR_TO_LEVEL[si];
            const on = profVal >= si;
            return (
              <button
                key={si}
                type="button"
                className={`skill-star${on ? ' is-on' : ''}`}
                role="radio"
                aria-checked={on}
                aria-label={`${si} ${si === 1 ? 'star' : 'stars'} – ${skProfLabel(lvl)}`}
                onClick={() => onSetStars(s.id, si)}
              >
                <Star aria-hidden="true" />
              </button>
            );
          })}
        </div>
        <span
          className={`skill-stars-caption${profVal ? '' : ' is-empty'}`}
          aria-live="polite"
        >
          {profVal ? skProfLabel(s.proficiency) : 'Tap a star to rate'}
        </span>
      </div>
    </div>
  );

  // ----- recency pills (required; pre-selected from inference) -----
  const band = skRecencyBand(s);
  const recMissing = band == null;
  const recErr = err && recMissing;
  const inferredBand =
    s.recency == null && skInferRecency(s) != null && !s.is_confirmed;
  let recLabelExtra = null;
  if (recMissing) {
    recLabelExtra = reqTag(recErr);
  } else if (inferredBand) {
    recLabelExtra = (
      <span className="skill-prof-hint">
        <Sparkles aria-hidden="true" />
        we guessed — tap to fix
      </span>
    );
  }
  const recRow = (
    <div
      id={`rec_${s.id}`}
      className={`skill-prof${recErr ? ' is-error' : ''}`}
    >
      <div className="skill-prof-label">
        Last used
        {recLabelExtra}
      </div>
      <div
        className="skill-prof-pills"
        role="group"
        aria-label={`Last used: ${s.name}`}
      >
        {SK_RECENCY.map((r) => {
          const on = band === r.key;
          return (
            <button
              key={r.key}
              type="button"
              className={`skill-pill${on ? ' selected' : ''}`}
              aria-pressed={on}
              onClick={() => onSetRecency(s.id, r.key)}
            >
              {r.label}
            </button>
          );
        })}
      </div>
    </div>
  );

  // ----- per-card "what's left" status (desktop only, todo only) -----
  // "Ready — just confirm" is dropped (the Confirm button already says it); the
  // count is hidden on mobile where the per-field Required markers cover it.
  let statusInline = null;
  if (
    !s.is_suppressed &&
    !s.is_confirmed &&
    !editing &&
    miss.length > 0 &&
    !isMobile
  ) {
    statusInline = (
      <span className="skill-status-inline skill-status--todo">
        <AlertCircle aria-hidden="true" />
        {miss.length} {miss.length === 1 ? 'thing to add' : 'things to add'}
      </span>
    );
  }

  // ----- actions -----
  const removeBtn = (
    <button
      type="button"
      className="skill-btn skill-btn--remove"
      onClick={() => onRequestRemove(s.id)}
      aria-label={`Remove ${s.name} from profile`}
    >
      <Trash2 aria-hidden="true" />
      Remove
    </button>
  );

  let actions;
  if (s.is_suppressed) {
    actions = (
      <div className="skill-actions">
        <button
          type="button"
          className="skill-btn skill-btn--restore"
          onClick={() => onRestore(s.id)}
        >
          <RotateCcw aria-hidden="true" />
          Restore to profile
        </button>
      </div>
    );
  } else if (confirmingRemove && !editing) {
    // Two-step removal: an inline confirmation in the action row.
    actions = (
      <div className="skill-actions skill-actions--remove">
        <span className="skill-remove-q">
          <AlertCircle aria-hidden="true" />
          Remove this skill from your profile?
        </span>
        <div className="skill-remove-actions">
          <button
            type="button"
            className="skill-btn skill-btn--danger"
            onClick={() => onSuppress(s.id)}
          >
            <Trash2 aria-hidden="true" />
            Remove
          </button>
          <button
            type="button"
            className="skill-btn skill-btn--ghost"
            onClick={() => onCancelRemove(s.id)}
          >
            Keep
          </button>
        </div>
      </div>
    );
  } else if (editing) {
    actions = (
      <div className="skill-actions">
        <button
          type="button"
          className="skill-btn skill-btn--confirm"
          onClick={() => onSaveEstimate(s.id, draft)}
        >
          <Check strokeWidth={2.5} aria-hidden="true" />
          Save
        </button>
        <button
          type="button"
          className="skill-btn skill-btn--ghost"
          onClick={() => onCancelEdit(s.id)}
        >
          Cancel
        </button>
      </div>
    );
  } else if (s.is_confirmed) {
    const dirty = !!s.dirtyEdit;
    if (dirty) {
      actions = (
        <div className="skill-actions">
          <button
            type="button"
            className="skill-btn skill-btn--confirm"
            onClick={() => onSaveConfirmedEdits(s.id)}
          >
            <Check strokeWidth={2.5} aria-hidden="true" />
            Save
          </button>
          <span className="skill-status-inline skill-status--todo">
            <AlertCircle aria-hidden="true" />
            Unsaved changes
          </span>
          {removeBtn}
        </div>
      );
    } else {
      actions = (
        <div className="skill-actions">
          <span className="skill-confirmed-tag">
            <Check strokeWidth={2} aria-hidden="true" />
            Confirmed by you
          </span>
          {removeBtn}
        </div>
      );
    }
  } else {
    actions = (
      <div className="skill-actions">
        <button
          type="button"
          className="skill-btn skill-btn--confirm"
          onClick={() => onConfirm(s.id)}
        >
          <Check strokeWidth={2.5} aria-hidden="true" />
          Confirm
        </button>
        {statusInline}
        {removeBtn}
      </div>
    );
  }

  let statusSuffix = ', needs review';
  if (s.is_suppressed) statusSuffix = ', removed';
  else if (s.is_confirmed) statusSuffix = ', confirmed';
  const cardLabel = `${s.name}${statusSuffix}`;

  // Top-right Collapse control: folds an expanded pending card back to its row
  // (mobile), or an expanded confirmed card back into the done pile.
  const canCollapsePending = isMobile && expandedPending && !s.is_confirmed;
  const canCollapseConfirmed = s.is_confirmed && !s.dirtyEdit && expanded;
  let collapseTop = null;
  if (!s.is_suppressed && (canCollapsePending || canCollapseConfirmed)) {
    const onCollapse = canCollapsePending
      ? onCollapsePending
      : onCollapseConfirmed;
    collapseTop = (
      <button
        type="button"
        className="skill-collapse-top"
        onClick={() => onCollapse(s.id)}
        title="Collapse"
        aria-label={`Collapse ${s.name}`}
      >
        <ChevronUp aria-hidden="true" />
      </button>
    );
  }

  return (
    <li
      className={`skill-card${s.is_confirmed ? ' is-confirmed' : ''}${
        s.is_suppressed ? ' is-suppressed' : ''
      }`}
      data-id={s.id}
      tabIndex={-1}
      aria-label={cardLabel}
    >
      <div className="skill-card-top">
        {canCollapseConfirmed ? (
          <button
            type="button"
            className="skill-id skill-id--toggle"
            onClick={() => onCollapseConfirmed(s.id)}
            aria-expanded="true"
            aria-label={`Collapse ${s.name}`}
          >
            <div className="skill-name-row">
              <span className="skill-name">{s.name}</span>
            </div>
          </button>
        ) : (
          <div className="skill-id">
            <div className="skill-name-row">
              <span className="skill-name">{s.name}</span>
            </div>
          </div>
        )}
        {collapseTop}
      </div>
      {/* Experience + Proficiency share one compact two-column row. While the
          months editor is open it needs full width, so they stack instead. */}
      {editing ? (
        <>
          {expBlock}
          {profRow}
        </>
      ) : (
        <div className="skill-duo">
          {expBlock}
          {profRow}
        </div>
      )}
      {recRow}
      {actions}
    </li>
  );
}
